export const LENS_LOAD_STAGES = Object.freeze({
    FETCH: "fetch",
    MODIFY: "modify",
    ENGINE: "engine",
    RUNTIME: "runtime",
});
export const LENS_LOAD_ERROR_CODES = Object.freeze({
    FETCH_FAILED: "FETCH_FAILED",
    INVALID_LENS_RESPONSE: "INVALID_LENS_RESPONSE",
    INVALID_LENS_SOURCE: "INVALID_LENS_SOURCE",
    MODIFICATION_FAILED: "MODIFICATION_FAILED",
    ENGINE_NOT_READY: "ENGINE_NOT_READY",
    ENGINE_LOAD_FAILED: "ENGINE_LOAD_FAILED",
    RUNTIME_EXCEPTION: "RUNTIME_EXCEPTION",
    REMOTE_ASSET_FAILED: "REMOTE_ASSET_FAILED",
    FIRST_FRAME_TIMEOUT: "FIRST_FRAME_TIMEOUT",
    UNKNOWN: "UNKNOWN",
});
/**
 * One stable error class for the public lens-loading boundary. The original
 * exception stays on `cause`; `toJSON()` emits only structured-clone-safe data.
 */
export class LensLoadError extends Error {
    code;
    stage;
    lensId;
    recoverable;
    originalPreserved;
    details;
    constructor(message, { code, stage, cause, lensId = null, recoverable = true, originalPreserved = true, details, }) {
        super(message, cause === undefined ? undefined : { cause });
        this.name = "LensLoadError";
        this.code = code;
        this.stage = stage;
        this.lensId = lensId;
        this.recoverable = recoverable;
        this.originalPreserved = originalPreserved;
        this.details = details;
    }
    toJSON() {
        return serializeLensLoadError(this);
    }
}
function messageOf(error) {
    if (error && typeof error === "object" && "message" in error) {
        return String(error.message || "");
    }
    return String(error || "Unknown lens-loading error");
}
/**
 * Convert errors from fetch, plugins, and LensCore into the stable public
 * taxonomy. Message matching is intentionally limited to errors we emit.
 * Unknown vendor errors keep the caller-provided stage and a generic code.
 *
 */
export function asLensLoadError(error, context = {}) {
    if (error instanceof LensLoadError)
        return error;
    const message = messageOf(error);
    let stage = context.stage ?? LENS_LOAD_STAGES.ENGINE;
    let code = context.code;
    if (!code && /returned HTML|auth redirect|not lens bytes/i.test(message)) {
        stage = LENS_LOAD_STAGES.FETCH;
        code = LENS_LOAD_ERROR_CODES.INVALID_LENS_RESPONSE;
    }
    else if (!code && /Failed to fetch|Could not fetch Lens|HTTP \d|network|CORS|mixed-content/i.test(message)) {
        stage = LENS_LOAD_STAGES.FETCH;
        code = LENS_LOAD_ERROR_CODES.FETCH_FAILED;
    }
    else if (!code && /unsupported source|missing lnsUrl|lensId could not be resolved/i.test(message)) {
        stage = LENS_LOAD_STAGES.FETCH;
        code = LENS_LOAD_ERROR_CODES.INVALID_LENS_SOURCE;
    }
    else if (!code && /Engine is not ready|must be initialized/i.test(message)) {
        stage = LENS_LOAD_STAGES.ENGINE;
        code = LENS_LOAD_ERROR_CODES.ENGINE_NOT_READY;
    }
    else if (!code && stage === LENS_LOAD_STAGES.MODIFY) {
        code = LENS_LOAD_ERROR_CODES.MODIFICATION_FAILED;
    }
    else if (!code && stage === LENS_LOAD_STAGES.RUNTIME) {
        code = LENS_LOAD_ERROR_CODES.RUNTIME_EXCEPTION;
    }
    return new LensLoadError(message || "Lens loading failed", {
        code: code ?? LENS_LOAD_ERROR_CODES.ENGINE_LOAD_FAILED,
        stage,
        cause: error,
        lensId: context.lensId,
        originalPreserved: context.originalPreserved ?? stage !== LENS_LOAD_STAGES.RUNTIME,
        details: context.details,
    });
}
export function serializeLensLoadError(error) {
    const normalized = error instanceof LensLoadError
        ? error
        : asLensLoadError(error, { code: LENS_LOAD_ERROR_CODES.UNKNOWN });
    return {
        name: "LensLoadError",
        message: normalized.message,
        stack: normalized.stack ?? null,
        code: normalized.code,
        stage: normalized.stage,
        lensId: normalized.lensId,
        recoverable: normalized.recoverable,
        originalPreserved: normalized.originalPreserved,
        ...(normalized.details === undefined ? {} : { details: normalized.details }),
    };
}
/** Restore the runtime class after an error crosses JSON or Electron IPC. */
export function deserializeLensLoadError(value) {
    if (!value || typeof value !== "object")
        return asLensLoadError(value);
    const candidate = value;
    if (!candidate.stage || !candidate.code)
        return asLensLoadError(candidate.message ?? value);
    const error = new LensLoadError(typeof candidate.message === "string" ? candidate.message : "Lens loading failed", {
        code: candidate.code,
        stage: candidate.stage,
        lensId: candidate.lensId == null ? null : String(candidate.lensId),
        recoverable: candidate.recoverable !== false,
        originalPreserved: candidate.originalPreserved !== false,
        details: candidate.details,
    });
    if (typeof candidate.stack === "string")
        error.stack = candidate.stack;
    return error;
}
/** Short copy suitable for renderer and Electron UI surfaces. */
export function lensLoadErrorPresentation(error) {
    const normalized = error instanceof LensLoadError ? error : asLensLoadError(error);
    const known = {
        FETCH_FAILED: ["Lens download failed", "Check the connection or lens URL and try again."],
        INVALID_LENS_RESPONSE: ["That was not a lens file", "The server returned a web page instead of lens data. Sign in again if needed."],
        INVALID_LENS_SOURCE: ["Lens source is invalid", "Choose a valid lens file or URL."],
        MODIFICATION_FAILED: ["Lens preparation failed", "The original lens was preserved and was not loaded."],
        ENGINE_NOT_READY: ["LensCore is still starting", "Wait a moment and try loading the lens again."],
        ENGINE_LOAD_FAILED: ["Lens could not be loaded", "LensCore rejected this lens package."],
        RUNTIME_EXCEPTION: ["Lens stopped unexpectedly", "A script or runtime operation failed after the lens loaded."],
        REMOTE_ASSET_FAILED: ["A lens asset is unavailable", "The lens loaded, but part of the effect may be missing."],
        FIRST_FRAME_TIMEOUT: ["Lens did not start rendering", "LensCore accepted the lens but did not produce a frame in time."],
        UNKNOWN: ["LensCore needs attention", "Something went wrong while loading the lens."],
    };
    const [title, message] = known[normalized.code] ?? known.UNKNOWN;
    return { title, message, detail: normalized.message, code: normalized.code, stage: normalized.stage };
}
//# sourceMappingURL=lensLoadError.js.map